// Sette Maps: the viewer of a monitoring's images, embedded in an iframe. The host creates a
// one-time code with the user's token and hands it to the iframe; the token never goes to it.
//
// VUE_APP_SETTE_MAPS_VIEWER_URL is the viewer page and VUE_APP_SETTE_MAPS_API its BFF, one pair per
// environment (.env.development/.env.homolog → hmg, .env.production → prd). The BFF answers CORS
// for the host pages of its allowlist, so this page calls it directly. Without both variables the
// feature stays hidden.
export const SETTE_MAPS_VIEWER_URL = process.env.VUE_APP_SETTE_MAPS_VIEWER_URL || "";
const SETTE_MAPS_API = process.env.VUE_APP_SETTE_MAPS_API || "";

export const setteMapsEnabled = (): boolean => !!SETTE_MAPS_VIEWER_URL && !!SETTE_MAPS_API;

export interface EmbedSessionRequest {
  resourceRef: string;
  parentOrigin: string;
  nonce: string;
  codeChallenge: string;
}

export class EmbedSessionError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
  }
}

// fetch instead of the shared axios instance: its 401 interceptor would log the user out when the
// BFF refuses a session or a check.
export async function createEmbedSession(
  accessToken: string,
  request: EmbedSessionRequest,
  signal: AbortSignal
): Promise<string> {
  const response = await fetch(`${SETTE_MAPS_API}/v1/embed-sessions`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${accessToken}`,
      "content-type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(request),
    signal,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || typeof body.code !== "string") {
    throw new EmbedSessionError(
      body.error || `http_${response.status}`,
      body.errorDescription || body.error || `HTTP ${response.status}`
    );
  }
  return body.code;
}

// The answer does not change while the user stays logged in, and every visit to the monitoring
// screen would otherwise spend a request of the BFF's rate limit. Only real answers are kept: a
// failure (429, 502, network) is retried on the next visit.
let cachedAccess: { token: string; allowed: boolean } | null = null;

// Whether the logged user's company may open the viewer from this page: the BFF checks its
// allowlist against the bearer's company and this page's origin. Any failure counts as "no", so
// the button simply stays hidden.
export async function checkEmbedAccess(accessToken: string, signal: AbortSignal): Promise<boolean> {
  if (cachedAccess && cachedAccess.token === accessToken) {
    return cachedAccess.allowed;
  }
  try {
    const response = await fetch(`${SETTE_MAPS_API}/v1/embed-access`, {
      headers: { authorization: `Bearer ${accessToken}`, accept: "application/json" },
      signal,
    });
    if (!response.ok) {
      return false;
    }
    const body = await response.json();
    const allowed = body.allowed === true;
    cachedAccess = { token: accessToken, allowed };
    return allowed;
  } catch {
    return false;
  }
}
